import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { GitCompareArrows, MapPin, TrendingUp, TrendingDown, Minus, Landmark, GraduationCap, Dumbbell, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => new Intl.NumberFormat('fr-DZ').format(v);

export default function ComparePage() {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useSEO({
    title: isFr ? 'Comparateur de quartiers - Beytam' : 'مقارنة الأحياء - بيتم',
    description: isFr ? 'Comparez les quartiers en Algérie : prix, tendances, écoles, monuments.' : 'قارن الأحياء في الجزائر: الأسعار، الاتجاهات، المدارس، المعالم.',
  });

  useEffect(() => {
    fetch(`${API}/api/price-map`).then(r => r.json()).then(d => setCities(d.cities || []));
  }, []);

  const cityData = cities.find(c => c.city === selectedCity);
  const neighborhoods = cityData?.neighborhoods || [];

  const toggleNeighborhood = (name) => {
    setSelected(prev => prev.includes(name) ? prev.filter(n => n !== name) : prev.length < 3 ? [...prev, name] : prev);
  };

  const compare = async () => {
    if (selected.length < 2) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ city: selectedCity });
      selected.forEach(n => params.append('n', n));
      const res = await fetch(`${API}/api/compare?${params}`);
      setResult(await res.json());
    } catch { setResult(null); }
    setLoading(false);
  };

  const TrendBadge = ({ trend }) => {
    if (trend === 'up') return <span className="flex items-center gap-1 text-emerald-600 text-xs font-semibold bg-emerald-50 px-2 py-1 rounded-full"><TrendingUp className="w-3 h-3" />{isFr ? 'Hausse' : 'صعود'}</span>;
    if (trend === 'down') return <span className="flex items-center gap-1 text-red-500 text-xs font-semibold bg-red-50 px-2 py-1 rounded-full"><TrendingDown className="w-3 h-3" />{isFr ? 'Baisse' : 'نزول'}</span>;
    return <span className="flex items-center gap-1 text-slate-500 text-xs font-semibold bg-slate-50 px-2 py-1 rounded-full"><Minus className="w-3 h-3" />{isFr ? 'Stable' : 'مستقر'}</span>;
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="compare-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-900 via-violet-800 to-indigo-700 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <GitCompareArrows className="w-6 h-6 text-violet-300" />
            <span className="text-violet-300 text-sm font-medium uppercase tracking-wider">{isFr ? 'Comparateur' : 'مقارنة'}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3" data-testid="compare-title">
            {isFr ? 'Comparez les quartiers' : 'قارن الأحياء'}
          </h1>
          <p className="text-violet-200 text-base sm:text-lg max-w-xl mx-auto">
            {isFr ? 'Sélectionnez 2 à 3 quartiers pour une comparaison détaillée.' : 'اختر 2 إلى 3 أحياء للمقارنة التفصيلية.'}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        {/* Selector */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-slate-100 mb-8" data-testid="compare-selector">
          <div className="mb-5">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">{isFr ? 'Choisissez une ville' : 'اختر مدينة'}</label>
            <div className="flex flex-wrap gap-2" data-testid="compare-city-selector">
              {cities.map(c => (
                <button key={c.city} onClick={() => { setSelectedCity(c.city); setSelected([]); setResult(null); }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${selectedCity === c.city ? 'bg-violet-800 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`compare-city-${c.city}`}
                >{isFr ? c.city : c.city_ar}</button>
              ))}
            </div>
          </div>

          {selectedCity && (
            <>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                {isFr ? `Sélectionnez 2 à 3 quartiers de ${selectedCity}` : `اختر 2 إلى 3 أحياء من ${cityData?.city_ar}`}
                <span className="text-slate-400 ml-2">({selected.length}/3)</span>
              </label>
              <div className="flex flex-wrap gap-2 mb-5" data-testid="compare-neighborhood-selector">
                {neighborhoods.map(n => (
                  <button key={n.name} onClick={() => toggleNeighborhood(n.name)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${selected.includes(n.name) ? 'bg-violet-100 border-violet-400 text-violet-800' : 'bg-white border-slate-200 text-slate-600 hover:border-violet-300'}`}
                    data-testid={`compare-n-${n.name}`}
                  >{selected.includes(n.name) && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />}{isFr ? n.name : n.name_ar || n.name}</button>
                ))}
              </div>
              <button onClick={compare} disabled={selected.length < 2 || loading}
                className="bg-violet-800 hover:bg-violet-900 text-white font-semibold py-3 px-8 rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto" data-testid="compare-submit">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <GitCompareArrows className="w-5 h-5" />}
                {isFr ? 'Comparer' : 'قارن'}
              </button>
            </>
          )}
        </div>

        {/* Results */}
        {result?.neighborhoods?.length >= 2 && <CompareResult result={result} isFr={isFr} TrendBadge={TrendBadge} />}
      </div>
    </div>
  );
}

function CompareResult({ result, isFr, TrendBadge }) {
  const ns = result.neighborhoods;
  const insights = result.insights;

  // Find min/max for coloring
  const maxSale = Math.max(...ns.map(n => n.avg_price_sqm_sale));
  const minSale = Math.min(...ns.map(n => n.avg_price_sqm_sale));
  const maxRent = Math.max(...ns.map(n => n.avg_price_sqm_rent));
  const minRent = Math.min(...ns.map(n => n.avg_price_sqm_rent));

  const rows = [
    { label: isFr ? 'Prix achat /m²' : 'سعر الشراء /م²', key: 'avg_price_sqm_sale', format: v => `${fmt(v)} DA`, best: minSale },
    { label: isFr ? 'Loyer /m²' : 'الإيجار /م²', key: 'avg_price_sqm_rent', format: v => `${fmt(v)} DA`, best: minRent },
    { label: isFr ? 'Tendance' : 'الاتجاه', key: 'trend', render: v => <TrendBadge trend={v} /> },
    { label: isFr ? 'Monuments' : 'المعالم', key: 'monuments_count', icon: Landmark },
    { label: isFr ? 'Écoles' : 'المدارس', key: 'schools_count', icon: GraduationCap },
    { label: isFr ? 'Activités' : 'الأنشطة', key: 'activities_count', icon: Dumbbell },
  ];

  return (
    <div className="pb-12 space-y-6" data-testid="compare-result">
      {/* Insights */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: isFr ? 'Moins cher (achat)' : 'الأرخص (شراء)', value: insights.cheapest_sale, color: 'emerald' },
          { label: isFr ? 'Moins cher (location)' : 'الأرخص (إيجار)', value: insights.cheapest_rent, color: 'amber' },
          { label: isFr ? 'Le + équipé' : 'الأفضل تجهيزاً', value: insights.most_equipped, color: 'violet' },
        ].map(i => (
          <div key={i.label} className={`bg-${i.color}-50 rounded-xl p-4 border border-${i.color}-100 text-center`} data-testid={`insight-${i.color}`}>
            <p className="text-xs text-slate-500 mb-1">{i.label}</p>
            <p className={`font-bold text-${i.color}-800 text-lg`}>{i.value}</p>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid="compare-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left p-4 text-sm font-medium text-slate-500 w-40"></th>
                {ns.map(n => (
                  <th key={n.name} className="p-4 text-center">
                    {n.image && <img src={n.image} alt={n.name} className="w-full h-24 object-cover rounded-lg mb-2" />}
                    <p className="font-bold text-slate-800">{n.name}</p>
                    <p className="text-xs text-slate-400">{n.name_ar}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.label} className="border-b border-slate-50">
                  <td className="p-4 text-sm font-medium text-slate-600">{row.label}</td>
                  {ns.map(n => {
                    const val = n[row.key];
                    const isBest = row.best !== undefined && val === row.best;
                    return (
                      <td key={n.name} className={`p-4 text-center text-sm font-semibold ${isBest ? 'text-emerald-700 bg-emerald-50/50' : 'text-slate-800'}`}>
                        {row.render ? row.render(val) : row.format ? row.format(val) : (
                          <span className="flex items-center justify-center gap-1">
                            {row.icon && <row.icon className="w-3.5 h-3.5 text-slate-400" />}
                            {val}
                          </span>
                        )}
                        {isBest && <span className="block text-[10px] text-emerald-600 mt-0.5">{isFr ? 'Meilleur' : 'الأفضل'}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Description row */}
              <tr className="border-b border-slate-50">
                <td className="p-4 text-sm font-medium text-slate-600">{isFr ? 'Ambiance' : 'الأجواء'}</td>
                {ns.map(n => (
                  <td key={n.name} className="p-4 text-xs text-slate-600 leading-relaxed">{n.ambiance}</td>
                ))}
              </tr>
              <tr>
                <td className="p-4 text-sm font-medium text-slate-600">{isFr ? 'Transport' : 'المواصلات'}</td>
                {ns.map(n => (
                  <td key={n.name} className="p-4 text-xs text-slate-600 leading-relaxed">{n.transport}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
